// @ts-nocheck
import { motion } from 'framer-motion';
import { YStack, XStack, Text, Paragraph } from 'tamagui';
import { Container } from './Container';
import { Eyebrow } from './Eyebrow';
import { useIsMobile } from '../hooks/useIsMobile';

const MotionDiv = motion.div;

/**
 * The page's structural unit.
 *
 * Every band is a cell in one continuous grid: a full-bleed rule above it, a
 * header cell, and a content cell. Rules run edge to edge rather than stopping
 * at the content column, which is what makes the layout read as a drafted sheet
 * instead of a stack of centred sections.
 *
 * `split` puts the header in a left cell with the content beside it, divided by
 * a vertical rule — the arrangement a long list of items wants, so the heading
 * stays in view while the eye runs down the rows.
 *
 * `tint` swaps the ground for the raised surface. Use it sparingly: alternating
 * every band turns the rhythm into stripes and the rules stop doing any work.
 */
export function GridBand({
  eyebrow,
  title,
  lede,
  split = false,
  tint = false,
  accent = '$amber9',
  children,
}) {
  const isMobile = useIsMobile();
  const stacked = isMobile || !split;

  const header = (
    <YStack gap="$4" flex={split && !isMobile ? 1 : undefined} maxWidth={split ? 460 : 720}>
      {eyebrow && <Eyebrow accent={accent}>{eyebrow}</Eyebrow>}

      {title && (
        <Text
          fontSize={isMobile ? 30 : 42}
          fontWeight="700"
          letterSpacing={-1.5}
          lineHeight={isMobile ? 36 : 48}
          color="$color12"
        >
          {title}
        </Text>
      )}

      {lede && (
        <Paragraph
          fontSize={isMobile ? '$4' : '$5'}
          color="$color8"
          lineHeight={isMobile ? 24 : 27}
        >
          {lede}
        </Paragraph>
      )}
    </YStack>
  );

  return (
    <YStack>
      <Container
        paddingHorizontal={0}
        borderTopWidth={1}
        borderColor="$color3"
        backgroundColor={tint ? '$color2' : '$background'}
      >
        {stacked ? (
          <YStack>
            <YStack
              paddingHorizontal="$4"
              paddingVertical={isMobile ? '$7' : '$9'}
              borderBottomWidth={children ? 1 : 0}
              borderColor="$color3"
            >
              {header}
            </YStack>

            {children && (
              <YStack paddingHorizontal="$4" paddingVertical={isMobile ? '$7' : '$9'}>
                <MotionDiv
                  initial={{ opacity: 0, y: 14 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-70px' }}
                  transition={{ duration: 0.45 }}
                >
                  {children}
                </MotionDiv>
              </YStack>
            )}
          </YStack>
        ) : (
          <XStack alignItems="stretch">
            <YStack
              flex={1}
              paddingHorizontal="$4"
              paddingVertical="$9"
              borderRightWidth={1}
              borderColor="$color3"
              minWidth={300}
            >
              {header}
            </YStack>

            <YStack flex={1.6} paddingHorizontal="$4" paddingVertical="$9" minWidth={340}>
              <MotionDiv
                initial={{ opacity: 0, y: 14 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-70px' }}
                transition={{ duration: 0.45 }}
              >
                {children}
              </MotionDiv>
            </YStack>
          </XStack>
        )}
      </Container>
    </YStack>
  );
}

/**
 * A bordered square holding an outlined glyph — the icon treatment that keeps
 * a flat page from going featureless without introducing fills or colour.
 */
export function IconCell({ children, size = 44 }) {
  return (
    <YStack
      width={size}
      height={size}
      borderWidth={1}
      borderColor="$color3"
      alignItems="center"
      justifyContent="center"
      flexShrink={0}
    >
      {children}
    </YStack>
  );
}
